// Ponta WebRTC da ligação no navegador do atendente. A mídia (Opus, ICE/DTLS/SRTP)
// vai direto entre o navegador e a Meta; o backend só repassa o SDP.
//
// A Meta não aceita candidatos ICE depois do SDP (sem "trickle"): a oferta e a
// resposta só saem depois que a coleta de candidatos termina (ou do timeout).

const ICE_GATHERING_TIMEOUT_MS = 3000;

export const waitForIceGathering = (pc, timeoutMs = ICE_GATHERING_TIMEOUT_MS) =>
  new Promise((resolve) => {
    if (pc.iceGatheringState === "complete") {
      resolve();
      return;
    }

    let timer = null;
    const finish = () => {
      clearTimeout(timer);
      pc.removeEventListener("icegatheringstatechange", onStateChange);
      pc.removeEventListener("icecandidate", onCandidate);
      resolve();
    };
    const onStateChange = () => {
      if (pc.iceGatheringState === "complete") finish();
    };
    // Candidato nulo = fim da coleta (navegadores que não disparam o state change).
    const onCandidate = (event) => {
      if (!event.candidate) finish();
    };

    timer = setTimeout(finish, timeoutMs);
    pc.addEventListener("icegatheringstatechange", onStateChange);
    pc.addEventListener("icecandidate", onCandidate);
  });

export class MicrophoneUnavailableError extends Error {}

const openMicrophone = async () => {
  if (!navigator.mediaDevices?.getUserMedia) {
    // HTTP sem TLS (ou navegador antigo): o navegador nem oferece o microfone.
    throw new MicrophoneUnavailableError("insecure-context");
  }

  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: false,
    });
  } catch (err) {
    throw new MicrophoneUnavailableError(err?.name || "denied");
  }
};

/**
 * Abre o microfone e monta a conexão. `onRemoteStream` recebe o áudio do contato;
 * `onConnectionState` acompanha a saúde da mídia (connected/failed/...).
 */
export const createCallPeer = async ({ iceServers, onRemoteStream, onConnectionState }) => {
  const localStream = await openMicrophone();
  const pc = new RTCPeerConnection({ iceServers, bundlePolicy: "max-bundle" });

  localStream.getAudioTracks().forEach((track) => pc.addTrack(track, localStream));

  pc.ontrack = (event) => {
    const [stream] = event.streams;
    onRemoteStream?.(stream || new MediaStream([event.track]));
  };
  pc.onconnectionstatechange = () => onConnectionState?.(pc.connectionState);

  return { pc, localStream };
};

/** Ligação feita: oferta completa (com candidatos) para o POST /calls/.../start. */
export const createOffer = async (pc) => {
  const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: false });
  await pc.setLocalDescription(offer);
  await waitForIceGathering(pc);
  return pc.localDescription.sdp;
};

export const applyAnswer = (pc, sdp) =>
  pc.setRemoteDescription({ type: "answer", sdp });

/** Ligação recebida: aplica a oferta da Meta e devolve a resposta completa. */
export const createAnswerForOffer = async (pc, offerSdp) => {
  await pc.setRemoteDescription({ type: "offer", sdp: offerSdp });
  const answer = await pc.createAnswer();
  await pc.setLocalDescription(answer);
  await waitForIceGathering(pc);
  return pc.localDescription.sdp;
};

export const closeCallPeer = (pc, localStream) => {
  try {
    localStream?.getTracks().forEach((track) => track.stop());
  } catch (err) {
    // trilha já encerrada
  }
  try {
    if (pc && pc.signalingState !== "closed") pc.close();
  } catch (err) {
    // conexão já fechada
  }
};

export const setMicrophoneMuted = (localStream, muted) => {
  localStream?.getAudioTracks().forEach((track) => {
    track.enabled = !muted;
  });
};
